import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, CartesianGrid, ComposedChart, Area,
} from 'recharts';
import api from '../api';

function formatPace(secPerKm) {
  if (!secPerKm) return '-';
  const mins = Math.floor(secPerKm / 60);
  const secs = Math.round(secPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatTime(seconds) {
  if (!seconds) return '-';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDistanceLabel(meters) {
  const m = parseInt(meters, 10);
  if (m >= 1000) return `${m / 1000}km`;
  return `${m}m`;
}

const cardStyle = { backgroundColor: '#0b0f14', borderRadius: '8px', padding: '16px', marginBottom: '20px', overflow: 'hidden' };
const chartWrap = { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' };
const chartInner = (minW) => ({ minWidth: minW ? `${minW}px` : undefined, width: '100%' });
const sectionTitle = { fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '16px' };

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ backgroundColor: '#111820', border: '1px solid #333', borderRadius: '6px', padding: '10px 14px', fontSize: '13px' }}>
      <div style={{ color: '#93a1b1', marginBottom: '4px' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#ff5a1f' }}>
          {p.name}: {formatter ? formatter(p.value, p.name) : p.value}
        </div>
      ))}
    </div>
  );
};

function Stats() {
  const [monthly, setMonthly] = useState([]);
  const [phases, setPhases] = useState([]);
  const [prs, setPrs] = useState(null);
  const [heatmap, setHeatmap] = useState([]);
  const [bestEfforts, setBestEfforts] = useState(null);
  const [metricsTrend, setMetricsTrend] = useState(null);
  const [metricsTab, setMetricsTab] = useState('regular');
  const [metricsAllTime, setMetricsAllTime] = useState(null);
  const [phaseProgress, setPhaseProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/stats/monthly'),
      api.get('/phases?gap_days=14'),
      api.get('/stats/personal-records'),
      api.get('/stats/heatmap'),
      api.get('/best-efforts/records'),
      api.get('/stats/metrics-trend'),
      api.get('/stats/phase-progress'),
    ]).then(([monthlyRes, phasesRes, prsRes, heatmapRes, bestEffortsRes, metricsTrendRes, phaseProgressRes]) => {
      setMonthly(monthlyRes.data.months || []);
      setPhases(phasesRes.data.phases || []);
      setPrs(prsRes.data);
      setHeatmap(heatmapRes.data.days || []);
      setBestEfforts(bestEffortsRes.data);
      setMetricsTrend(metricsTrendRes.data);
      setPhaseProgress(phaseProgressRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: '#93a1b1' }}>Loading stats...</div>;
  }

  // Prepare phase pace data for chart
  const phasePaceData = phases
    .filter(p => p.avg_pace_sec_per_km && p.total_runs > 2)
    .map(p => ({
      name: `P${p.phase_number}`,
      label: `Phase ${p.phase_number}`,
      pace: Math.round(p.avg_pace_sec_per_km),
      paceMin: formatPace(p.avg_pace_sec_per_km),
      bestPace: Math.round(p.best_pace_sec_per_km || 0),
      bestPaceMin: formatPace(p.best_pace_sec_per_km),
      runs: p.total_runs,
      distance: p.total_distance_km,
      start: p.start_date?.substring(0, 7) || '',
    }));

  // Monthly chart data
  const monthlyChart = monthly.map(m => ({
    month: m.month,
    distance: m.distance_km,
    runs: m.runs,
    pace: m.avg_pace_sec_per_km ? Math.round(m.avg_pace_sec_per_km) : null,
    elevation: m.elevation_m,
  }));

  // Heatmap: build 52-week grid
  const heatmapMap = {};
  heatmap.forEach(d => { heatmapMap[d.date] = d; });

  const today = new Date();
  const weeks = [];
  for (let w = 51; w >= 0; w--) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(today);
      date.setDate(date.getDate() - (w * 7 + (6 - d)));
      const key = date.toISOString().split('T')[0];
      const data = heatmapMap[key];
      week.push({
        date: key,
        distance: data ? data.distance_km : 0,
        runs: data ? data.runs : 0,
      });
    }
    weeks.push(week);
  }

  const prEntries = prs?.personal_records ? Object.entries(prs.personal_records) : [];

  // Best efforts data
  const allTime = bestEfforts?.all_time || {};
  const allTimeSprint = bestEfforts?.all_time_sprint || {};
  const currentPhase = bestEfforts?.current_phase || {};
  const currentPhaseRuns = bestEfforts?.current_phase_runs || 0;
  const distanceKeys = Object.keys(allTime).sort((a, b) => parseInt(a) - parseInt(b));
  const hasSpeedTracker = distanceKeys.length > 0;

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#fff', marginBottom: '24px' }}>Statistics</h1>

      {/* Speed Tracker - Best Efforts Comparison */}
      {hasSpeedTracker && (
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Speed Tracker</h2>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '18px' }}>
            Current phase: {currentPhaseRuns} run{currentPhaseRuns !== 1 ? 's' : ''}
          </p>

          {/* Desktop table view */}
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 1fr 1fr 80px',
              gap: '6px',
              padding: '8px 10px',
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#666',
              borderBottom: '1px solid #1e2936',
              minWidth: '460px',
            }}>
              <div>Dist</div>
              <div>Segment Best</div>
              <div>Sprint Best</div>
              <div>Phase Best</div>
              <div style={{ textAlign: 'right' }}>vs Phase</div>
            </div>

            {/* Rows */}
            {distanceKeys.map((distKey) => {
              const at = allTime[distKey];
              const sp = allTimeSprint[distKey];
              const cp = currentPhase[distKey];
              let diffSeconds = null;
              let diffColor = '#93a1b1';
              if (at && cp) {
                diffSeconds = cp.time_seconds - at.time_seconds;
                const diffPct = at.time_seconds > 0 ? (diffSeconds / at.time_seconds) * 100 : 0;
                if (diffSeconds <= 0) {
                  diffColor = '#3ddc84'; // green - PR
                } else if (diffPct <= 5) {
                  diffColor = '#3ddc84'; // green - within 5%
                } else if (diffPct <= 15) {
                  diffColor = '#f5a623'; // yellow - 5-15% gap
                } else {
                  diffColor = '#ff4d4f'; // red - >15% gap
                }
              }

              return (
                <div key={distKey} style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 1fr 1fr 80px',
                  gap: '6px',
                  padding: '8px 10px',
                  alignItems: 'center',
                  borderBottom: '1px solid #1e1e35',
                  fontSize: '13px',
                  minWidth: '460px',
                }}>
                  <div style={{ color: '#e8edf2', fontWeight: 600 }}>
                    {formatDistanceLabel(distKey)}
                  </div>
                  <div>
                    {at ? (
                      <Link to={`/activity/${at.activity_id}`} style={{ color: '#ff5a1f', textDecoration: 'none', fontWeight: 600 }}>
                        {formatTime(at.time_seconds)}
                      </Link>
                    ) : <span style={{ color: '#555' }}>-</span>}
                    {at && <div style={{ color: '#666', fontSize: '10px' }}>{formatPace(at.pace_sec_per_km)}/km</div>}
                  </div>
                  <div>
                    {sp ? (
                      <Link to={`/activity/${sp.activity_id}`} style={{ color: '#3ddc84', textDecoration: 'none', fontWeight: 600 }}>
                        {formatTime(sp.time_seconds)}
                      </Link>
                    ) : <span style={{ color: '#555' }}>-</span>}
                    {sp && <div style={{ color: '#666', fontSize: '10px' }}>{formatPace(sp.pace_sec_per_km)}/km</div>}
                  </div>
                  <div>
                    {cp ? (
                      cp.activity_id ? (
                        <Link to={`/activity/${cp.activity_id}`} style={{ color: '#60a5fa', textDecoration: 'none', fontWeight: 600 }}>
                          {formatTime(cp.time_seconds)}
                        </Link>
                      ) : <span style={{ color: '#e8edf2' }}>{formatTime(cp.time_seconds)}</span>
                    ) : <span style={{ color: '#555' }}>-</span>}
                    {cp && <div style={{ color: '#666', fontSize: '10px' }}>{formatPace(cp.pace_sec_per_km)}/km</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {at && cp ? (
                      diffSeconds <= 0 ? (
                        <span style={{ color: '#3ddc84', fontWeight: 700, fontSize: '11px' }}>PR!</span>
                      ) : (
                        <span style={{ color: diffColor, fontSize: '12px' }}>+{Math.round(diffSeconds)}s</span>
                      )
                    ) : <span style={{ color: '#555' }}>-</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase Progress */}
      {phaseProgress && ((phaseProgress.regular || []).length > 1 || (phaseProgress.interval || []).length > 1) && (() => {
        const fmtDelta = (d) => {
          if (d === null || d === undefined) return '-';
          const sign = d < 0 ? '↓' : d > 0 ? '↑' : '·';
          const color = d < 0 ? '#3ddc84' : d > 0 ? '#ff4d4f' : '#888';
          const mins = Math.floor(Math.abs(d) / 60);
          const secs = Math.round(Math.abs(d) % 60);
          const txt = mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${Math.abs(d).toFixed(0)}s`;
          return <span style={{ color, fontWeight: 700 }}>{sign} {txt}/km</span>;
        };
        const buildChart = (data) => data.map((d, i) => ({
          idx: i + 1,
          date: d.date.substring(5),
          pace: d.pace_sec_per_km,
          paceLabel: formatPace(d.pace_sec_per_km),
        }));
        const reg = phaseProgress.regular || [];
        const intv = phaseProgress.interval || [];
        const regSum = phaseProgress.regular_summary;
        const intvSum = phaseProgress.interval_summary;
        const block = (title, color, summary, data) => {
          if (!summary || data.length < 2) return null;
          const chartData = buildChart(data);
          return (
            <div style={{ flex: '1 1 280px', minWidth: 0, backgroundColor: '#0b0f14', borderRadius: '6px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color }}>{title}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>{summary.session_count} sessions</div>
              </div>
              <div style={{ fontSize: '11px', color: '#93a1b1', marginBottom: '4px' }}>
                {formatPace(summary.first_pace)} → <span style={{ color: '#fff', fontWeight: 700 }}>{formatPace(summary.latest_pace)}</span>/km &nbsp;{fmtDelta(summary.delta)}
              </div>
              <div style={{ fontSize: '10px', color: '#666', marginBottom: '8px' }}>
                phase best: {formatPace(summary.best_pace)}/km
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <LineChart data={chartData} margin={{ top: 4, right: 6, left: -10, bottom: 0 }}>
                  <CartesianGrid stroke="#222" strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fill: '#555', fontSize: 9 }} />
                  <YAxis tick={{ fill: '#555', fontSize: 9 }} domain={['auto', 'auto']} tickFormatter={(v) => formatPace(v)} reversed width={42} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0b0f14', border: '1px solid #333', borderRadius: '4px', fontSize: '11px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(v) => [`${formatPace(v)}/km`, 'pace']}
                  />
                  <Line type="monotone" dataKey="pace" stroke={color} strokeWidth={2} dot={{ r: 3, fill: color }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          );
        };
        return (
          <div style={cardStyle}>
            <h2 style={sectionTitle}>Phase Progress</h2>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '12px' }}>
              Pace per session within the current phase. Line trending up = getting faster. Green delta = improving.
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {block('Regular Runs', '#ff5a1f', regSum, reg)}
              {block('Interval Avg Rep', '#f5a623', intvSum, intv)}
            </div>
            {!regSum && !intvSum && (
              <div style={{ color: '#666', fontSize: '12px' }}>Not enough sessions in this phase yet.</div>
            )}
          </div>
        );
      })()}

      {/* Run Quality Metrics */}
      {metricsTrend && (metricsTrend.regular?.length > 1 || metricsTrend.interval?.length > 1) && (() => {
        const activeData = metricsAllTime || metricsTrend;
        const data = metricsTab === 'regular' ? (activeData.regular || []) : (activeData.interval || []);
        const hasRegular = (activeData.regular || []).length > 1;
        const hasInterval = (activeData.interval || []).length > 1;
        const latest = data.length > 0 ? data[data.length - 1] : null;
        const prev = data.length > 1 ? data[data.length - 2] : null;
        const scoreDiff = latest && prev ? latest.consistency - prev.consistency : null;
        const isAllTime = metricsAllTime !== null;
        return (
          <div style={cardStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', flexWrap: 'wrap', gap: '6px' }}>
              <h2 style={{ ...sectionTitle, marginBottom: 0 }}>Run Quality</h2>
              <div style={{ display: 'flex', gap: '4px' }}>
                {hasRegular && (
                  <button onClick={() => setMetricsTab('regular')}
                    style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      backgroundColor: metricsTab === 'regular' ? '#ff5a1f' : '#111820', color: metricsTab === 'regular' ? '#fff' : '#666' }}>
                    Runs
                  </button>
                )}
                {hasInterval && (
                  <button onClick={() => setMetricsTab('interval')}
                    style={{ padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, border: 'none', cursor: 'pointer',
                      backgroundColor: metricsTab === 'interval' ? '#f5a623' : '#111820', color: metricsTab === 'interval' ? '#0b0f14' : '#666' }}>
                    Intervals
                  </button>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '14px' }}>
              <button onClick={async () => {
                if (isAllTime) { setMetricsAllTime(null); }
                else {
                  try {
                    const res = await api.get('/stats/metrics-trend?phase_only=false');
                    setMetricsAllTime(res.data);
                  } catch {}
                }
              }}
                style={{ padding: '0', border: 'none', backgroundColor: 'transparent', color: '#666', fontSize: '11px', cursor: 'pointer' }}>
                {isAllTime ? 'Show current phase only' : 'Show all time'} {isAllTime ? '\u25B2' : '\u25BC'}
              </button>
            </div>

            {/* Current metrics summary */}
            {latest && (
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
                  <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Consistency</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: latest.consistency >= 80 ? '#3ddc84' : latest.consistency >= 60 ? '#f5a623' : '#ff4d4f' }}>
                    {latest.consistency}
                  </div>
                  {scoreDiff !== null && scoreDiff !== 0 && (
                    <div style={{ fontSize: '10px', color: scoreDiff > 0 ? '#3ddc84' : '#ff4d4f' }}>
                      {scoreDiff > 0 ? '+' : ''}{scoreDiff} vs last
                    </div>
                  )}
                </div>
                <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
                  <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Fade</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: Math.abs(latest.fade_s) <= 5 ? '#3ddc84' : latest.fade_s > 15 ? '#ff4d4f' : '#f5a623' }}>
                    {latest.fade_s > 0 ? '+' : ''}{latest.fade_s}s
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>{latest.fade_s > 3 ? 'slowing' : latest.fade_s < -3 ? 'speeding up' : 'even'}</div>
                </div>
                <div style={{ backgroundColor: '#111820', borderRadius: '6px', padding: '10px 14px', textAlign: 'center', flex: '1 1 80px' }}>
                  <div style={{ fontSize: '10px', color: '#666', textTransform: 'uppercase' }}>Decay</div>
                  <div style={{ fontSize: '22px', fontWeight: 700, color: Math.abs(latest.decay_s_per_rep || latest.decay_s_per_lap || 0) <= 2 ? '#3ddc84' : '#f5a623' }}>
                    {(latest.decay_s_per_rep || latest.decay_s_per_lap || 0).toFixed(1)}s
                  </div>
                  <div style={{ fontSize: '10px', color: '#666' }}>per {metricsTab === 'interval' ? 'rep' : 'lap'}</div>
                </div>
              </div>
            )}

            {/* Consistency trend chart */}
            {data.length > 2 && (
              <div style={chartWrap}>
                <div style={chartInner(400)}>
                  <ResponsiveContainer width="100%" height={160}>
                    <ComposedChart data={data.slice(-20)} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                      <XAxis dataKey="date" tick={{ fill: '#666', fontSize: 9 }} interval={Math.max(0, Math.floor(data.slice(-20).length / 6))} />
                      <YAxis tick={{ fill: '#666', fontSize: 10 }} domain={[0, 100]} width={35} />
                      <Tooltip content={<CustomTooltip formatter={(v, name) => {
                        if (name === 'Consistency') return `${v}/100`;
                        if (name === 'Fade') return `${v}s`;
                        return v;
                      }} />} />
                      <Area type="monotone" dataKey="consistency" name="Consistency" fill="#3ddc8415" stroke="#3ddc84" strokeWidth={2} dot={{ r: 3, fill: '#3ddc84' }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Personal Records */}
      {(prEntries.length > 0 || prs?.best_1km_split) && (
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Personal Records</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px' }}>
            {prs?.best_1km_split && (
              <div style={{ backgroundColor: '#111820', borderRadius: '8px', padding: '16px', textAlign: 'center', border: '1px solid #ff5a1f33' }}>
                <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: '#93a1b1', marginBottom: '4px' }}>Best 1km Split</div>
                <div style={{ fontSize: '26px', fontWeight: 700, color: '#ff5a1f' }}>{formatTime(prs.best_1km_split.time)}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                  {formatPace(prs.best_1km_split.time)}/km
                </div>
              </div>
            )}
            {prEntries.map(([dist, pr]) => (
              <Link to={`/activity/${pr.activity_id}`} key={dist} style={{ textDecoration: 'none' }}>
                <div style={{ backgroundColor: '#111820', borderRadius: '8px', padding: '16px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s', border: '1px solid transparent' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: '#93a1b1', marginBottom: '4px' }}>{dist} PR</div>
                  <div style={{ fontSize: '26px', fontWeight: 700, color: '#3ddc84' }}>{formatTime(pr.time_seconds)}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                    {formatPace(pr.pace_sec_per_km)}/km &middot; {formatDate(pr.date)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Phase Pace Trend */}
      {phasePaceData.length > 0 && (
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Pace Across Phases</h2>
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>Only phases with 3+ runs shown. Lower = faster.</p>
          <div style={chartWrap}>
            <div style={chartInner(500)}>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={phasePaceData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                  <XAxis dataKey="name" tick={{ fill: '#666', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => formatPace(v)} reversed width={45} />
                  <Tooltip content={<CustomTooltip formatter={(v, name) => {
                    if (name === 'Avg Pace' || name === 'Best Pace') return formatPace(v);
                    if (name === 'Distance') return `${v} km`;
                    return v;
                  }} />} />
                  <Area type="monotone" dataKey="pace" name="Avg Pace" fill="#ff5a1f15" stroke="#ff5a1f" strokeWidth={2} dot={{ fill: '#ff5a1f', r: 3 }} />
                  <Line type="monotone" dataKey="bestPace" name="Best Pace" stroke="#3ddc84" strokeWidth={1.5} strokeDasharray="4 4" dot={{ fill: '#3ddc84', r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Distance + Runs */}
      {monthlyChart.length > 0 && (
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Monthly Volume</h2>
          <div style={chartWrap}>
            <div style={chartInner(600)}>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={monthlyChart} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                  <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 9 }} interval={2} angle={-45} textAnchor="end" height={45} />
                  <YAxis yAxisId="km" tick={{ fill: '#666', fontSize: 10 }} width={35} />
                  <YAxis yAxisId="runs" orientation="right" tick={{ fill: '#666', fontSize: 10 }} width={30} />
                  <Tooltip content={<CustomTooltip formatter={(v, name) => {
                    if (name === 'Distance') return `${v} km`;
                    if (name === 'Pace') return formatPace(v);
                    return v;
                  }} />} />
                  <Bar yAxisId="km" dataKey="distance" name="Distance" fill="#ff5a1f66" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="runs" type="monotone" dataKey="runs" name="Runs" stroke="#60a5fa" strokeWidth={1.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Pace Trend */}
      {monthlyChart.length > 0 && (
        <div style={cardStyle}>
          <h2 style={sectionTitle}>Monthly Avg Pace</h2>
          <div style={chartWrap}>
            <div style={chartInner(500)}>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={monthlyChart.filter(m => m.pace)} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e2936" />
                  <XAxis dataKey="month" tick={{ fill: '#666', fontSize: 9 }} interval={3} angle={-45} textAnchor="end" height={45} />
                  <YAxis tick={{ fill: '#666', fontSize: 10 }} domain={['auto', 'auto']} tickFormatter={(v) => formatPace(v)} reversed width={45} />
                  <Tooltip content={<CustomTooltip formatter={(v) => formatPace(v)} />} />
                  <Line type="monotone" dataKey="pace" name="Avg Pace" stroke="#ff5a1f" strokeWidth={2} dot={{ r: 2, fill: '#ff5a1f' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Activity Heatmap */}
      <div style={cardStyle}>
        <h2 style={sectionTitle}>Running Heatmap (Last 12 Months)</h2>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* Month labels row */}
          <div style={{ display: 'flex', gap: '2px', minWidth: '600px', marginBottom: '4px', paddingLeft: '18px' }}>
            {weeks.map((week, wi) => {
              const monthStr = week[0]?.date?.substring(0, 7) || '';
              const prevMonthStr = wi > 0 ? (weeks[wi - 1][0]?.date?.substring(0, 7) || '') : '';
              const showLabel = monthStr !== prevMonthStr;
              const label = showLabel ? new Date(monthStr + '-15').toLocaleDateString('en-US', { month: 'short' }) : '';
              return (
                <div key={wi} style={{ width: '10px', fontSize: '9px', color: '#666', whiteSpace: 'nowrap', overflow: 'visible' }}>
                  {label}
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '2px', minWidth: '600px' }}>
            {/* Day labels */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '4px', justifyContent: 'flex-start' }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <div key={i} style={{ width: '14px', height: '10px', fontSize: '9px', color: '#666', lineHeight: '10px', textAlign: 'right' }}>
                  {i % 2 === 1 ? d : ''}
                </div>
              ))}
            </div>
            {weeks.map((week, wi) => (
              <div key={wi} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {week.map((day, di) => {
                  let bg = '#111820';
                  if (day.runs > 0) {
                    if (day.distance > 5) bg = '#ff5a1f';
                    else if (day.distance > 3) bg = '#ff5a1faa';
                    else bg = '#ff5a1f55';
                  }
                  return (
                    <div key={di} title={`${day.date}: ${day.runs > 0 ? `${day.distance}km` : 'Rest day'}`}
                      style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: bg }} />
                  );
                })}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px', fontSize: '11px', color: '#666', marginLeft: '32px' }}>
            <span>Less</span>
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#111820' }} />
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#ff5a1f55' }} />
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#ff5a1faa' }} />
            <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#ff5a1f' }} />
            <span>More</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stats;
